/**
 * Cashflow display rules. Web never sums money: totals, averages and per-period
 * figures come from the server, and so does which periods are deficits. The
 * only arithmetic is `netDelta` (the difference of two server figures) and
 * `deltaPct` (a ratio of two server figures). Neither sums rows.
 */
import { percentOf } from '../../platform/percentOf'
import type { LocalDate } from '../../platform/localDate'
import { MONTH_LONG, MONTH_SHORT, periodTitle, type Granularity } from './cashflowPeriod'
import type { Cashflow, Period, Totals } from './cashflowWire'

/**
 * The ONE deficit truth: the server's `totals.deficit_buckets`, keyed by start
 * date. The tile names these, and the chart and the month table mark a period
 * as a deficit iff its `start` is in this set — nothing on the web recomputes
 * "complete and net below zero", so a backend refinement of the rule moves all
 * three together (gate N3). The list is every deficit bucket in the window,
 * not a clipped sample: `aggregate.totals` builds it from all complete buckets.
 */
export function deficitStarts(deficits: Cashflow['deficitPeriods']): ReadonlySet<LocalDate> {
  return new Set(deficits.map((d) => d.start))
}

export function deltaPct(current: number, prior: number | null): number | null {
  if (prior === null || prior === 0) return null
  // Math.round can yield -0 for a tiny fall; -0 would render as a signed zero.
  return percentOf(current - prior, Math.abs(prior)) || 0
}

/**
 * A `deltaPct` as a sub line under a row or tile, against a named prior:
 * "Up 20% vs September", "Down 5% vs September", "No change vs September".
 * Null (no prior to compare) is no line at all, never "0%" (M3-R4).
 */
export function shortDelta(pct: number | null, phrase: string): string | null {
  if (pct === null) return null
  if (pct === 0) return `No change ${phrase}`
  return `${pct > 0 ? 'Up' : 'Down'} ${Math.abs(pct)}% ${phrase}`
}

/** A server share as drawn: a whole percent. Formatting, not arithmetic on money. */
export const sharePercent = (pct: number): string => `${Math.round(pct)}%`

/** Difference of two server-supplied nets, not a sum over rows. */
export function netDelta(totals: Totals, prior: Totals | null): number | null {
  return prior === null ? null : totals.netCents - prior.netCents
}

/** The axis tick: `'Aug'`, `'Q4 2025'`, `'2025'`. Date parts, never local parsing. */
export function periodLabel(p: Period, g: Granularity): string {
  const [y = '', m = '1'] = p.start.split('-')
  if (g === 'year') return y
  if (g === 'quarter') return `Q${Math.floor((Number(m) - 1) / 3) + 1} ${y}`
  return MONTH_SHORT[Number(m) - 1] ?? ''
}

/** The long form: `'December 2025'`, `'Q4 2026'`, `'2025'` — the period header's own title. */
export function periodName(p: Period, g: Granularity): string {
  return periodTitle(p.start, g)
}

/** `''`, `'EUR'`, `'EUR and XOF'`, `'EUR, GBP and XOF'`. */
export function andList(items: readonly string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

/**
 * The runway's `until` as drawn: "14 October", with the year only when it is
 * not `today`'s ("5 January 2027"). Date parts, never `new Date(str)`, so a
 * user west of UTC does not get the day before.
 */
export function untilDate(until: LocalDate, today: LocalDate): string {
  const [y = '', m = '1', d = '1'] = until.split('-')
  const day = `${Number(d)} ${MONTH_LONG[Number(m) - 1] ?? ''}`
  return y === today.slice(0, 4) ? day : `${day} ${y}`
}
